import createTranslateFunction from '../../../../i18n/createTranslateFunction';
import { useDispatch } from 'react-redux';
import { UpdateDeliveryFormData } from '../../../../redux/slices/deliveryFormSlice';
import {
    FocusableComponentLayout,
    FocusContext,
    FocusDetails,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation';
import { useRef, KeyboardEvent, useEffect } from 'react';

type OrderNotesInputProps = {
    focusKey: string;
    onRowFocus: (layout: FocusableComponentLayout, props: unknown, details: FocusDetails) => void;
};

const OrderNotesInput = ({ focusKey: focusKeyParam, onRowFocus }: OrderNotesInputProps) => {
    const OrderNotesTranslateFunction = createTranslateFunction(
        'checkout_screen.delivery_form.modals.note',
    );
    const inputRef = useRef<HTMLInputElement>(null);

    const { ref, focusKey, focused } = useFocusable({
        focusKey: focusKeyParam,
        onFocus: (layout, props, details) => {
            // Ensure input receives keyboard input when component gets focus
            inputRef.current?.focus();
            onRowFocus(layout, props, details);
        },
        onBlur: () => {
            setTimeout(() => {
                inputRef.current?.blur();
                // @ts-expect-error - webOS keyboard API is not typed
                if (window.webOS?.keyboard?.hide) {
                    // @ts-expect-error - webOS keyboard API is not typed
                    window.webOS.keyboard.hide();
                }
            }, 100);
        },
    });

    const dispatch = useDispatch();
    const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(UpdateDeliveryFormData({ notes: event.target.value }));
    };

    // Global key event listener as fallback
    useEffect(() => {
        const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
            console.log('Global Keyboard Event:', {
                key: event.key,
                code: event.code,
                type: event.type,
                keyCode: event.keyCode,
                which: event.which,
                target: event.target,
                focused: focused,
                activeElement: document.activeElement,
            });

            if ((event.key === 'Enter' || event.key === 'OK' || event.keyCode === 13) && focused) {
                event.preventDefault();
                setTimeout(() => {
                    inputRef.current?.blur();
                    // @ts-expect-error - webOS keyboard API is not typed
                    window.webOS?.keyboard?.hide?.();
                }, 100);
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [focused]);

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        console.log('Input Keyboard Event:', {
            key: event.key,
            code: event.code,
            type: event.type,
            keyCode: event.keyCode,
            which: event.which,
            target: event.target,
        });

        if (event.key === 'Enter' || event.key === 'Insert') {
            event.preventDefault();
            setTimeout(() => {
                // @ts-expect-error - webOS keyboard API is not typed
                if (window.webOS?.keyboard?.hide) {
                    // @ts-expect-error - webOS keyboard API is not typed
                    window.webOS.keyboard.hide();
                }
                inputRef.current?.blur();
            }, 100);
            return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const inputValue = event.currentTarget.value;
            // If input has value, always handle arrow keys within input
            if (inputValue) {
                event.stopPropagation();
                event.preventDefault();
                const cursorPosition = inputRef.current?.selectionStart || 0;

                // For RTL text, we need to reverse the arrow key behavior
                if (event.key === 'ArrowLeft' && cursorPosition < inputValue.length) {
                    inputRef.current?.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
                } else if (event.key === 'ArrowRight' && cursorPosition > 0) {
                    inputRef.current?.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
                }
            } else {
                // If input is empty, allow navigation
                return;
            }
        }
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div
                ref={ref}
                className={`flex flex-col w-[80%] p-[1vw] rounded-[1.2vw] transition-all duration-200
                    ${focused ? 'bg-white bg-opacity-10' : ''}`}
            >
                <span className={`text-[1.4vw] ${focused ? 'text-white' : 'text-gray-400'}`}>
                    {`${OrderNotesTranslateFunction('title')}`}
                </span>
                <input
                    ref={inputRef}
                    type="text"
                    dir="auto"
                    onChange={handleNoteChange}
                    onKeyDown={handleKeyDown}
                    placeholder={OrderNotesTranslateFunction('placeholder')}
                    className={`w-full h-[7vw] mt-[1vw] px-[3vw] text-[1.2vw] rounded-[1.2vw] text-start border-2 placeholder-gray-300 border-white text-white
                        [&::placeholder]:text-right
                        ${focused ? 'bg-white bg-opacity-20 border-gray-400' : 'bg-gray-400/10 hover:bg-gray-400/20'}`}
                />
            </div>
        </FocusContext.Provider>
    );
};

export default OrderNotesInput;
